import { createSelector } from '@reduxjs/toolkit';
import { TStoreState } from 'redux/types';
import { TJotformWarning } from 'redux/reducers/jotform/types';
import {
	TPpwrJotform,
	TPpwrJotformListResponse,
	TPpwrJotformSearchRequest,
	TPpwrEmSettings,
	TPpwrEmSettingsListResponse,
	TPpwrEmSettingsSearchRequest,
	TPpwrFee,
	TPpwrFeeListResponse,
	TPpwrFeeSearchRequest,
	TPpwrPackagingType,
	TPpwrPackagingTypeListResponse,
	TPpwrPackagingTypeSearchRequest,
	TPpwrStore,
	TPpwrEmPackagingTypeListResponse,
	TPpwrEmPackagingTypeSearchRequest,
	TPpwrEmCountryExclusionListResponse,
	TPpwrEmCountryExclusionSearchRequest,
} from './types';

export const getPpwrState = (state: TStoreState): TPpwrStore => state.ppwr;

export const getPpwrPackagingTypes = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrPackagingTypeListResponse | null => state.packagingTypes,
);

export const getPpwrPackagingTypesLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.packagingTypesLoading,
);

export const getPpwrLastPackagingTypesParams = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrPackagingTypeSearchRequest | null => state.lastPackagingTypesParams,
);

export const getPpwrPackagingTypeOptions = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrPackagingType[] | null => state.packagingTypeOptions,
);

export const getPpwrPackagingTypeOptionsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.packagingTypeOptionsLoading,
);

export const getPpwrPackagingTypeDetails = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrPackagingType | null => state.packagingTypeDetails,
);

export const getPpwrPackagingTypeDetailsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.packagingTypeDetailsLoading,
);

export const getPpwrPackagingTypeSaveLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.packagingTypeSaveLoading,
);

export const getPpwrPackagingTypeSaveSuccess = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.packagingTypeSaveSuccess,
);

export const getPpwrFees = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrFeeListResponse | null => state.fees,
);

export const getPpwrFeesLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.feesLoading,
);

export const getPpwrLastFeesParams = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrFeeSearchRequest | null => state.lastFeesParams,
);

export const getPpwrFeeDetails = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrFee | null => state.feeDetails,
);

export const getPpwrFeeDetailsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.feeDetailsLoading,
);

export const getPpwrFeeSaveLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.feeSaveLoading,
);

export const getPpwrFeeSaveSuccess = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.feeSaveSuccess,
);

export const getPpwrEmSettings = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrEmSettingsListResponse | null => state.emSettings,
);

export const getPpwrEmSettingsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emSettingsLoading,
);

export const getPpwrLastEmSettingsParams = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrEmSettingsSearchRequest | null => state.lastEmSettingsParams,
);

export const getPpwrEmSettingsDetails = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrEmSettings | null => state.emSettingsDetails,
);

export const getPpwrEmSettingsDetailsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emSettingsDetailsLoading,
);

export const getPpwrEmSettingsSaveLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emSettingsSaveLoading,
);

export const getPpwrEmSettingsSaveSuccess = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emSettingsSaveSuccess,
);

export const getPpwrJotforms = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrJotformListResponse | null => state.jotforms,
);

export const getPpwrJotformsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.jotformsLoading,
);

export const getPpwrLastJotformsParams = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrJotformSearchRequest | null => state.lastJotformsParams,
);

export const getPpwrJotformDetails = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrJotform | null => state.jotformDetails,
);

export const getPpwrJotformDetailsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.jotformDetailsLoading,
);

export const getPpwrActivateServiceLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.activateServiceLoading,
);

export const getPpwrActivateServiceSuccess = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.activateServiceSuccess,
);

export const getPpwrActivateServiceWarning = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TJotformWarning | null => state.activateServiceWarning,
);

export const getPpwrFeeOptions = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrFee[] | null => state.feeOptions,
);

export const getPpwrFeeOptionsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.feeOptionsLoading,
);

export const getPpwrEmPackagingTypes = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrEmPackagingTypeListResponse | null => state.emPackagingTypes,
);

export const getPpwrEmPackagingTypesLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emPackagingTypesLoading,
);

export const getPpwrLastEmPackagingTypesParams = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrEmPackagingTypeSearchRequest | null => state.lastEmPackagingTypesParams,
);

export const getPpwrEmPackagingTypeSaveLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emPackagingTypeSaveLoading,
);

export const getPpwrEmPackagingTypeSaveSuccess = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emPackagingTypeSaveSuccess,
);

export const getPpwrEmPackagingTypeDocumentUploadLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emPackagingTypeDocumentUploadLoading,
);

export const getPpwrDownloadingEmPackagingTypeDocumentId = createSelector(
	[getPpwrState],
	(state: TPpwrStore): number | null => state.downloadingEmPackagingTypeDocumentId,
);

export const getPpwrDeletingEmPackagingTypeDocumentId = createSelector(
	[getPpwrState],
	(state: TPpwrStore): number | null => state.deletingEmPackagingTypeDocumentId,
);

export const getPpwrEmCountryExclusions = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrEmCountryExclusionListResponse | null => state.emCountryExclusions,
);

export const getPpwrEmCountryExclusionsLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emCountryExclusionsLoading,
);

export const getPpwrLastEmCountryExclusionsParams = createSelector(
	[getPpwrState],
	(state: TPpwrStore): TPpwrEmCountryExclusionSearchRequest | null =>
		state.lastEmCountryExclusionsParams,
);

export const getPpwrEmCountryExclusionSaveLoading = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emCountryExclusionSaveLoading,
);

export const getPpwrEmCountryExclusionSaveSuccess = createSelector(
	[getPpwrState],
	(state: TPpwrStore): boolean => state.emCountryExclusionSaveSuccess,
);
